import { useState, KeyboardEvent, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmailTagInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  className?: string;
}

export function EmailTagInput({ value, onChange, placeholder = "Enter email address...", className }: EmailTagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Email validation regex
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const addEmail = (email: string) => {
    const trimmedEmail = email.trim();
    if (trimmedEmail && isValidEmail(trimmedEmail) && !value.includes(trimmedEmail)) {
      onChange([...value, trimmedEmail]);
      setInputValue('');
    }
  };

  const removeEmail = (index: number) => {
    const newEmails = value.filter((_, i) => i !== index);
    onChange(newEmails);
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditValue(value[index]);
  };

  const saveEdit = () => {
    if (editingIndex !== null) {
      const trimmedEmail = editValue.trim();
      if (trimmedEmail && isValidEmail(trimmedEmail)) {
        const newEmails = [...value];
        newEmails[editingIndex] = trimmedEmail;
        onChange(newEmails);
      }
      setEditingIndex(null);
      setEditValue('');
    }
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeEmail(value.length - 1);
    }
  };

  const handleEditKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue);
    }
  };

  const handleEditBlur = () => {
    saveEdit();
  };

  return (
    <div className={cn(
      "min-h-[38px] border rounded-md p-2 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 bg-background",
      className
    )}>
      <div className="flex flex-wrap gap-1.5 items-center">
        {value.map((email, index) => (
          <div key={index} className="flex items-center">
            {editingIndex === index ? (
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleEditKeyDown}
                onBlur={handleEditBlur}
                className="h-7 text-xs min-w-[120px] w-auto border-primary"
                autoFocus
              />
            ) : (
              <Badge 
                variant="secondary" 
                className="group cursor-pointer hover:bg-secondary/80 gap-1.5 text-xs py-1 pr-1 transition-all hover:shadow-sm"
                onClick={() => startEditing(index)}
                title="Click to edit"
              >
                <span className="max-w-[200px] truncate">{email}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEmail(index);
                  }}
                  className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5 transition-colors group-hover:bg-secondary-foreground/10"
                  title="Remove email"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        ))}
        
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          placeholder={value.length === 0 ? placeholder : "Add another email..."}
          className="border-none shadow-none focus-visible:ring-0 flex-1 min-w-[140px] h-7 text-xs px-1 bg-transparent"
        />
      </div>
      
      {/* Helper text - shows on focus */}
      {(inputValue || editingIndex !== null) && (
        <div className="text-xs text-muted-foreground mt-1">
          Press Enter or comma to add • Click email to edit • Click × to remove
        </div>
      )}
    </div>
  );
}
